export type CollectionDomain = 'bidding' | 'procurement' | 'people' | 'rubrics';

export type CollectionArchetype =
  // Bidding Domain
  | 'bidding_proposal'
  | 'bidding_legal'
  | 'bidding_company_profile'
  | 'bidding_past_forms'
  // Procurement Domain
  | 'procurement_rfp'
  | 'procurement_legal'
  | 'procurement_supplier_data'
  | 'procurement_compliance'
  | 'global_qa'
  // People Domain
  | 'people_cv'
  // Rubrics Domain
  | 'rubrics_rubric'
  | 'uncategorized';

export const DOMAIN_ARCHETYPES: Record<CollectionDomain, CollectionArchetype[]> = {
  bidding: [
    'bidding_proposal',
    'bidding_legal',
    'bidding_company_profile',
    'bidding_past_forms',
    'people_cv',
    'uncategorized',
  ],
  procurement: [
    'procurement_rfp',
    'procurement_legal',
    'procurement_supplier_data',
    'procurement_compliance',
    'global_qa',
  ],
  people: ['people_cv'],
  rubrics: ['rubrics_rubric'],
};

export const ARCHETYPE_LABELS: Record<CollectionArchetype, string> = {
  bidding_proposal: 'Past Proposals',
  bidding_legal: 'Legal Clauses',
  bidding_company_profile: 'Company Profiles & Case Studies',
  bidding_past_forms: 'Corporate Data & Past Forms',

  procurement_rfp: 'Past Issued RFPs',
  procurement_legal: 'Legal & Compliance Frameworks',
  procurement_supplier_data: 'Supplier Data & Forms',
  procurement_compliance: 'Compliance Documents',
  global_qa: 'Global Q&A',

  people_cv: 'Team & CVs',
  rubrics_rubric: 'Evaluation Matrices',
  uncategorized: 'Uncategorized',
};

export interface ArchetypeDescription {
  what: string;
  usage: string;
}

export const ARCHETYPE_DESCRIPTIONS: Record<CollectionArchetype, ArchetypeDescription> = {
  bidding_proposal: {
    what: 'Completed proposals, methodologies, and executive summaries.',
    usage: 'Used to understand how you pitch your services and structure past responses.',
  },
  bidding_legal: {
    what: 'Standard terms, conditions, and contract clauses.',
    usage: 'Used to answer legal, commercial, and compliance questions.',
  },
  bidding_company_profile: {
    what: 'Marketing brochures, capability statements, and project case studies.',
    usage: 'Used for narrative company overviews and detailed project experience.',
  },
  bidding_past_forms: {
    what: 'Filled questionnaires, insurance certificates, and company details (like ABNs or addresses).',
    usage: 'Used to extract short, factual data to fill out strict forms and tables.',
  },

  procurement_rfp: {
    what: 'Historical tender documents issued by buyers.',
    usage: 'Used to analyze market trends and typical requirements.',
  },
  procurement_legal: {
    what: 'Buyer-side legal templates and standard contracts.',
    usage: 'Used to baseline standard terms for procurement.',
  },
  procurement_supplier_data: {
    what: 'Data collected from suppliers and vendors.',
    usage: 'Used to analyze supplier capabilities and market positioning.',
  },
  procurement_compliance: {
    what: 'Regulatory and compliance standards required for procurement.',
    usage: 'Used to verify adherence to industry regulations.',
  },
  global_qa: {
    what: 'Historical Q&A documents, spreadsheets, and finalized clarification responses.',
    usage:
      'Used to automatically answer incoming supplier questions based on previously approved responses.',
  },

  people_cv: {
    what: 'Resumes, bios, and qualification documents for your staff.',
    usage: 'Used to extract skills and project experience to form your proposed project team.',
  },
  rubrics_rubric: {
    what: 'Scoring guides and evaluation matrices used to grade proposals.',
    usage: 'Used to evaluate drafts against buyer criteria.',
  },
  uncategorized: {
    what: 'Files that have not yet been reviewed or categorized.',
    usage: 'These files will not be used in specific context searches until categorized.',
  },
};

export interface KnowledgeDocument {
  id: string; // The Firestore docId
  fileName: string;
  sourceUrl: string; // gs:// or sharepoint url
  storageType: 'gcs' | 'sharepoint';
  collectionType: CollectionArchetype;
  syncStatus: 'pending' | 'processing' | 'ready' | 'error';
  globalSummary?: string;
  updatedAt?: string; // ISO string or Firestore Timestamp
  domain?: 'grant' | 'tender' | 'both';
}
